import { isBefore, isSameDay } from 'date-fns'
import {
  isPossiblePhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js'

import { dateToUTCDate } from '../date/dateToUTCDate'

import type {
  ValidationFunction,
  Validator,
  ValidationError,
} from '@ngb-frontend/shared/types'
import type { CountryCode } from 'libphonenumber-js'

const validationDefaults = {
  minMileageValue: 1,
  maxMileageValue: 9999999,
}

const compose =
  <T>(...validators: ReadonlyArray<Validator<T> | undefined>) =>
  (value: T) =>
    validators.reduce<ValidationError | undefined>(
      (error, validator) => error || validator?.(value),
      undefined,
    )

const apply =
  <T>(
    validator: ValidationFunction<T>,
    validationError: ValidationError,
  ): Validator<T> =>
  (value: T) =>
    validator(value) ? undefined : validationError

const required = <T>(value: T) => {
  if (Array.isArray(value)) return !!value.length
  if (typeof value === 'string') return !!value.trim()

  return !!value
}

const minLength =
  (length: number) =>
  <T>(value: T) =>
    Array.isArray(value) || typeof value === 'string'
      ? value.length >= length
      : false

const validDate = <T>(value: T) =>
  (value instanceof Date && !Number.isNaN(value.getTime())) ||
  (typeof value === 'string' && !Number.isNaN(Date.parse(value)))

const minValue =
  (minValue: number) =>
  <T>(value: T) =>
    Number(value) >= minValue

const maxValue =
  (minValue: number) =>
  <T>(value: T) =>
    Number(value) <= minValue

const minMaxValue =
  (min: number, max: number) =>
  <T>(value: T) =>
    minValue(min)(value) && maxValue(max)(value)

const isBeforeDate = (d: string, dToComp: string, strict = false) => {
  const date = new Date(d)
  const dateToCompare = new Date(dToComp)

  if (!strict && isSameDay(date, dateToCompare)) return true

  return isBefore(date, dateToCompare)
}

const isBeforeTime = (t: string, sToComp: string) => {
  return t <= sToComp
}

const isAfterTime = (t: string, sToComp: string) => {
  return t >= sToComp
}

const isDateNotBefore =
  (beforeDate: string, required?: boolean) => (value: string) => {
    if (!required && !value?.trim()) {
      return true
    }

    if (!validDate(beforeDate)) {
      throw new Error('The "beforeDate" parameter is not a valid date string')
    }

    return isBeforeDate(beforeDate, value)
  }

const isDateNotDisabled = (utcDisabledDates: number[]) => (value: string) => {
  return !utcDisabledDates.includes(dateToUTCDate(value))
}

const isDateNotWeekend = (d: string) => {
  const dayOfWeek = new Date(d).getDay()
  const isWeekend = dayOfWeek === 6 || dayOfWeek === 0

  return !isWeekend
}

const isTimeNotBefore =
  (beforeTime: string, required?: boolean) => (value: string) => {
    if (!required && !value?.trim()) {
      return true
    }
    return isBeforeTime(beforeTime, value)
  }

const isTimeNotAfter =
  (afterTime: string, required?: boolean) => (value: string) => {
    if (!required && !value?.trim()) {
      return true
    }
    return isAfterTime(afterTime, value)
  }

const isValidPhoneNumber = (country: CountryCode) => (value: string) => {
  return (
    isPossiblePhoneNumber(value, country) &&
    validatePhoneNumberLength(value, country) !== 'INVALID_LENGTH'
  )
}

/**
 * Pragmatic email validator.
 *
 * Accepts the addresses this application sends to — local parts with dots and
 * plus-addressing, subdomains and multi-label TLDs — and requires at least one dot
 * in the domain, so single-label hosts like "a@a" are rejected even though the mail
 * spec would permit them. The accept/reject surface is documented in validation.test.ts.
 *
 * Replaces the previous hand-rolled RFC-822 pattern, whose control-character ranges
 * (\xNN escapes) each raised a SonarQube reliability finding (NGRE2E-4037).
 */
const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

export const isEmail = (value: string) => EMAIL_PATTERN.test(value)

const isPhoneTooShort = (country: CountryCode) => (value: string) => {
  return validatePhoneNumberLength(value, country) !== 'TOO_SHORT'
}

const isPhoneTooLong = (country: CountryCode) => (value: string) => {
  return validatePhoneNumberLength(value, country) !== 'TOO_LONG'
}

export const validation = {
  validationDefaults,
  compose,
  apply,
  isValidPhoneNumber,
  required,
  minLength,
  validDate,
  minValue,
  maxValue,
  minMaxValue,
  isBeforeDate,
  isDateNotBefore,
  isDateNotDisabled,
  isDateNotWeekend,
  isTimeNotAfter,
  isTimeNotBefore,
  isPhoneTooLong,
  isPhoneTooShort,
  isEmail,
}
